// Users-list ordering shared by Partner users, Customer users and Admin & Staff:
// A→Z by name (first or last name), or by Last sign-in / Role / Status.
// Clicking a sortable header sorts by it; clicking it again returns to A→Z.
import { useMemo, useState } from "react";
import { ArrowDownAZ, ArrowDown, ArrowUpDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const LAST_NAME_KEY = "users.sortByLastName";
const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });
const STATUS_RANK = { active: 0, invited: 1, pending: 1, suspended: 2, deactivated: 3 };

const lastNameOf = (name = "") => {
  const parts = name.trim().split(/\s+/).filter((p) => /[\p{L}\p{N}]/u.test(p));
  return parts.length > 1 ? `${parts[parts.length - 1]} ${parts.slice(0, -1).join(" ")}` : parts.join(" ");
};

export function useUserSort(users) {
  const [sort, setSort] = useState(null);   // null = A→Z by name
  const [byLast, setByLastState] = useState(() => localStorage.getItem(LAST_NAME_KEY) === "1");
  const setByLast = (v) => { setByLastState(v); localStorage.setItem(LAST_NAME_KEY, v ? "1" : "0"); };
  const toggle = (key) => setSort((s) => (s === key ? null : key));

  const sorted = useMemo(() => {
    const nameKey = (u) => (byLast ? lastNameOf(u.name) : u.name || "");
    const byName = (a, b) => collator.compare(nameKey(a), nameKey(b));
    const cmp = {
      last_login: (a, b) => (b.last_login || "").localeCompare(a.last_login || ""),   // newest first, never last
      role: (a, b) => collator.compare(a.role_label || a.role || "", b.role_label || b.role || ""),
      status: (a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9),
    }[sort];
    return [...(users || [])].sort((a, b) => (cmp && cmp(a, b)) || byName(a, b));
  }, [users, sort, byLast]);

  return { sorted, sort, toggle, byLast, setByLast };
}

export function SortHeader({ label, sortKey, sort, onToggle, className = "", testid }) {
  const active = sort === sortKey;
  return (
    <button type="button" onClick={() => onToggle(sortKey)} data-testid={testid}
            title={active ? "Click to return to A–Z" : `Sort by ${label.toLowerCase()}`}
            className={`inline-flex items-center gap-0.5 uppercase tracking-wide transition-colors hover:text-slate-700 ${active ? "text-primary" : ""} ${className}`}>
      {label}
      {active ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-40" />}
    </button>
  );
}

export function NameSortHint({ sort, byLast, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 ${sort ? "" : "text-primary"}`} data-testid={testid}>
      {!sort && <ArrowDownAZ className="h-3 w-3" />}{byLast ? "by last name" : "A–Z"}
    </span>
  );
}

export function lastSignInLabel(iso) {
  if (!iso) return "Never";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function LastNameSwitch({ checked, onChange, testid }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-500">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      Sort by last name
    </label>
  );
}
